import React, { useEffect, useState } from "react";
import { Breadcrumb, Button, Col, DatePicker, Popover, Row, Select, Space } from "antd";
import { FilterOutlined, LeftOutlined, RightOutlined } from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import { useTranslation } from "react-i18next";
import locale from "antd/es/date-picker/locale/nl_NL";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../constants/DateConstant";
import Loading from "../../../components/shared-components/Loading";
import { MARKETING_CODES, PRODUCT_COMPANY_GROUPS } from "../../../constants/ProductConstant";
import { useOrders } from "../../../data/hooks/firestore/useOrders";
import { useInvoices } from "../../../data/hooks/firestore/useInvoices";
import { useCustomers } from "../../../data/hooks/firestore/useCustomers";
import { ORDER_STATUS_CANCELED, ORDER_STATUS_CANCELLED } from "../../../constants/OrderConstant";
import { CHEFS_FARM, GROWY } from "../../../constants/CompanyConstants";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { reports } from "../../../urls";
import useReportFilters from "../../../data/hooks/url-parameters/useReportFilters";
import { StyledSelect } from "../../../styles/sharedStyles";
import useIsMobile from "../../../hooks/useIsMobile";
import styled from "styled-components";

const { RangePicker } = DatePicker;
const { Option } = Select;

const ReportDetail = ({ title, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobileView = useIsMobile();
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const [filteredOrders, setFilteredOrders] = useState([]);

  // Use the shared report filters hook
  const {
    isCustomRange,
    customDateRange,
    companyFilter,
    marketingCodeFilter,
    activeTimeFilter,
    dateRange,
    dateRangeText,
    onChangeRange,
    onPreviousWeek,
    onNextWeek,
    onCurrentWeek,
    handleCompanyChange,
    handleMarketingCodeChange,
    getURLParamsString,
    isFiltersDefault,
    resetFilters,
  } = useReportFilters();

  // Use the useOrders hook with calculated date range - but get more data for proper filtering
  const { data: orders, loading: ordersLoading, error: ordersError } = useOrders(30, dateRange);

  // Fetch invoices with same date range
  const { data: invoices, loading: invoicesLoading, error: invoicesError } = useInvoices(30, dateRange);

  // Fetch customers for company filtering
  const { customers, loading: customersLoading } = useCustomers();

  const loading = ordersLoading || invoicesLoading || customersLoading;

  // Generate breadcrumb items based on current path
  const getBreadcrumbItems = () => {
    const urlParams = getURLParamsString();
    const urlSuffix = urlParams ? `?${urlParams}` : "";
    const pathParts = location.pathname.split("/").filter(Boolean);

    const items = [
      {
        title: <Link to={`${reports}${urlSuffix}`}>{t("reports")}</Link>,
      },
    ];

    // If we're in a sub-report, add intermediate breadcrumbs
    if (pathParts.includes("products")) {
      items.push({
        title: <Link to={`${reports}/products${urlSuffix}`}>{t("products_report")}</Link>,
      });

      if (pathParts.includes("availability")) {
        items.push({
          title: <span>{t("product_availability_report")}</span>,
        });
      }
    } else if (pathParts.includes("customers")) {
      items.push({
        title: <span>{t("customers_report")}</span>,
      });
    } else if (pathParts.includes("deliveries")) {
      items.push({
        title: <span>{t("deliveries_report")}</span>,
      });
    } else if (pathParts.includes("complaints")) {
      items.push({
        title: <span>{t("complaints_report")}</span>,
      });
    } else if (pathParts.includes("stock")) {
      items.push({
        title: <span>{t("stock_report")}</span>,
      });
    }

    return items;
  };

  // Filter orders based on company filter only (date filtering will be done in individual report components)
  useEffect(() => {
    if (orders) {
      // Only filter out cancelled orders and apply company filter
      let filtered = orders.filter(
        (order) => order.farmorderStatus !== ORDER_STATUS_CANCELED && order.farmorderStatus !== ORDER_STATUS_CANCELLED,
      );

      // Apply company filter based on customer.company
      if (companyFilter !== "All") {
        filtered = filtered.filter((order) => {
          const customer = customers.find((c) => c.id === order.farmorderCustomerId);
          const customerCompany = customer?.company;

          if (companyFilter === GROWY) {
            return customerCompany === GROWY;
          } else if (companyFilter === CHEFS_FARM) {
            return customerCompany === CHEFS_FARM || !customerCompany;
          }
          return false;
        });
      }

      // Apply marketing code filter based on customer.marketingCode
      if (marketingCodeFilter !== "All") {
        filtered = filtered.filter((order) => {
          const customer = customers.find((c) => c.id === order.farmorderCustomerId);
          return customer?.marketingCode === marketingCodeFilter;
        });
      }

      setFilteredOrders(filtered);
    }
  }, [orders, companyFilter, marketingCodeFilter, customers]);

  if (loading) return <Loading />;
  if (ordersError) return <div>Error loading orders: {ordersError.message}</div>;
  if (invoicesError) return <div>Error loading invoices: {invoicesError.message}</div>;

  return (
    <>
      <Row gutter={16} className={"mb-2"}>
        <Col xs={24} sm={24} md={16} lg={16} xl={15} xxl={14}>
          <Flex alignItems="center" style={{ gap: "16px" }}>
            <Breadcrumb items={getBreadcrumbItems()} />
            {!isMobileView && (
              <>
                <StyledSelect
                  style={{ minWidth: 140, marginLeft: 20 }}
                  $isFiltered={companyFilter !== "All"}
                  value={companyFilter}
                  onChange={handleCompanyChange}
                >
                  <Option value="All">{t("all_companies")}</Option>
                  {PRODUCT_COMPANY_GROUPS.map((group) => (
                    <Option key={group.value} value={group.value}>
                      {group.label}
                    </Option>
                  ))}
                </StyledSelect>
                <StyledSelect
                  style={{ minWidth: 160, marginLeft: 8 }}
                  $isFiltered={marketingCodeFilter !== "All"}
                  value={marketingCodeFilter}
                  onChange={handleMarketingCodeChange}
                >
                  <Option value="All">{t("all_customer_types")}</Option>
                  {MARKETING_CODES.map((code) => (
                    <Option key={code.value} value={code.value}>
                      {code.label}
                    </Option>
                  ))}
                </StyledSelect>
              </>
            )}
          </Flex>
          <h1 style={{ margin: "10px 0" }}>{t(title)}</h1>
        </Col>
        <Col xs={24} sm={24} md={8} lg={8} xl={9} xxl={10}>
          <Flex justifyContent={!isMobileView ? "end" : "start"} alignItems="center">
            {!isMobileView ? (
              <Space>
                <Button icon={<LeftOutlined />} onClick={onPreviousWeek} disabled={isCustomRange} />
                <CustomButton
                  onClick={onCurrentWeek}
                  disabled={isCustomRange}
                  type={activeTimeFilter === "current_week" ? "secondary" : "default"}
                >
                  {t("current_week")}
                </CustomButton>
                <Button icon={<RightOutlined />} onClick={onNextWeek} disabled={isCustomRange} />
                <RangePicker
                  onChange={onChangeRange}
                  locale={locale}
                  format={DATE_FORMAT_DD_MM_YYYY}
                  allowClear
                  value={customDateRange}
                />
                {!isFiltersDefault && (
                  <Button onClick={resetFilters} type="primary">
                    {t("reset_filters")}
                  </Button>
                )}
              </Space>
            ) : (
              <Popover
                content={
                  <FilterPopoverContent>
                    <div>
                      <FilterLabel>{t("company")}</FilterLabel>
                      <Select
                        value={companyFilter}
                        onChange={handleCompanyChange}
                        style={{ width: "100%" }}
                        size="large"
                      >
                        <Option value="All">{t("all_companies")}</Option>
                        {PRODUCT_COMPANY_GROUPS.map((group) => (
                          <Option key={group.value} value={group.value}>
                            {group.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <FilterLabel>{t("customer_type")}</FilterLabel>
                      <Select
                        value={marketingCodeFilter}
                        onChange={handleMarketingCodeChange}
                        style={{ width: "100%" }}
                        size="large"
                      >
                        <Option value="All">{t("all_customer_types")}</Option>
                        {MARKETING_CODES.map((code) => (
                          <Option key={code.value} value={code.value}>
                            {code.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <FilterLabel>{t("date_range")}</FilterLabel>
                      <Space direction="vertical" style={{ width: "100%" }}>
                        <Button
                          onClick={onCurrentWeek}
                          disabled={isCustomRange}
                          type={activeTimeFilter === "current_week" ? "primary" : "default"}
                          style={{ width: "100%" }}
                          size="large"
                        >
                          {t("current_week")}
                        </Button>
                        <RangePicker
                          onChange={onChangeRange}
                          locale={locale}
                          format={DATE_FORMAT_DD_MM_YYYY}
                          allowClear
                          value={customDateRange}
                          style={{ width: "100%" }}
                          size="large"
                          popupClassName="single-month-range-picker"
                        />
                      </Space>
                    </div>
                    <FilterButtonsContainer>
                      <Button
                        onClick={() => {
                          resetFilters();
                          setFilterPopoverVisible(false);
                        }}
                        style={{ flex: 1, marginTop: 8 }}
                      >
                        {t("reset")}
                      </Button>
                      <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                        {t("apply")}
                      </Button>
                    </FilterButtonsContainer>
                  </FilterPopoverContent>
                }
                title={t("filters")}
                trigger="click"
                open={filterPopoverVisible}
                onOpenChange={setFilterPopoverVisible}
                placement="bottomLeft"
              >
                <Button
                  icon={<FilterOutlined />}
                  style={{
                    borderColor: !isFiltersDefault ? "#FF4B33" : "",
                    color: !isFiltersDefault ? "#FF4B33" : "",
                  }}
                />
              </Popover>
            )}
          </Flex>
        </Col>
      </Row>

      {React.cloneElement(children, {
        orders: filteredOrders,
        invoices: invoices || [],
        dateRange: dateRange,
        dateRangeText: dateRangeText,
        companyFilter,
        marketingCodeFilter,
        loading,
      })}
    </>
  );
};

const CustomButton = ({ type, children, ...props }) => {
  const getButtonStyle = () => {
    if (type === "secondary") {
      return {
        backgroundColor: "#f0f0f0",
        borderColor: "#d9d9d9",
        color: "#595959",
      };
    }
    return {};
  };

  return (
    <Button {...props} style={{ ...getButtonStyle(), ...props.style }}>
      {children}
    </Button>
  );
};

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
`;

export default ReportDetail;
